import debounce from 'debounce-promise';
import React, { useEffect, useMemo, useState } from 'react';
import { OnChangeValue, SelectInstance } from 'react-select';
import AsyncSelect from 'react-select/async';
import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId';
import { IBaseSelectProps, ISelectCustomComponents, ISelectOption, ISelectStyleProps } from './selectTypes';
import { selectStyles } from './selectStyles';
import { DropdownSearchIndicator, SelectErrorMessageContainer } from './SelectComponents';
import { createLoadOptionsFunc } from './selectFilterHelpers';

const DEFAULT_DEBOUNCE_MS_ASYNC = 300;

export interface IMultiSelectProps extends IBaseSelectProps {
  onMultiSelectChange?: (options?: ISelectOption[]) => void;
  closeMenuOnSelect?: boolean;
  /** Initial values */
  defaultSelectedOptions?: ISelectOption[];
  /** Selection options are not shown in the dropdown. Default is true */
  hideSelectedOptions?: boolean;
  /** Selection options are not shown in the main container. Default is false */
  hideSelectedValues?: boolean;
}

export interface ISelectProps extends IBaseSelectProps {
  value?: ISelectOption;
  onChange?: (option?: ISelectOption) => void;
}

export interface IBaseMultiSelectProps extends ISelectProps, IMultiSelectProps {
  isMulti?: boolean;
}

export const BaseSelect = styled(AsyncSelect)<ISelectStyleProps>`
  ${selectStyles};
`;

const BaseMultiSelect = React.forwardRef<HTMLElement, Omit<IBaseMultiSelectProps, 'ref'>>(function BaseMultiSelect(
  {
    loadOptions,
    debounceInMs = DEFAULT_DEBOUNCE_MS_ASYNC,
    options,
    invalid,
    defaultSelectedOptions: defaultValues,
    hideSelectedValues,
    selectCustomComponents,
    isMulti = true,
    menuPosition = 'absolute',
    ...rest
  },
  ref
) {
  const debouncedLoadOptionsAsync = useMemo(() => debounce(loadOptions ?? createLoadOptionsFunc(options ?? []), debounceInMs), [debounceInMs]);
  const normalLoadOptions = createLoadOptionsFunc(options ?? []);
  const { generateTestIdClassName } = useTestId(rest, 'MultiSelectObsolete');

  const [isInvalid, setIsInvalid] = useState(invalid);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    setIsInvalid(invalid);
  }, [invalid]);

  const onChange = (option: OnChangeValue<ISelectOption, false>) => {
    if (rest.required) {
      setIsInvalid(!option);
    }

    if (rest.onChange) {
      rest.onChange(option || undefined);
    }
  };

  const onMultiSelectChange = (newOptions: OnChangeValue<ISelectOption, true>) => {
    if (rest.required) {
      setIsInvalid(!newOptions || newOptions.length === 0);
    }

    if (rest.onMultiSelectChange) {
      rest.onMultiSelectChange([...newOptions]);
    }
  };

  const onBlur = () => {
    if (rest.required) {
      setIsInvalid(!rest.value);
    }

    if (rest.onBlur) {
      rest.onBlur();
    }
  };

  if (loadOptions) {
    selectCustomComponents = selectCustomComponents || ({} as ISelectCustomComponents);
    selectCustomComponents.DropdownIndicator = DropdownSearchIndicator;
  }

  const onSelectRef = (e: SelectInstance) => {
    if (!ref) {
      return;
    }

    if (typeof ref === 'function') {
      ref(e?.controlRef);
    } else {
      ref.current = e?.controlRef;
    }
  };

  return (
    <>
      <BaseSelect
        // @ts-ignore
        ref={onSelectRef}
        menuPlacement='auto'
        menuPosition={menuPosition}
        classNamePrefix='react-select'
        components={selectCustomComponents}
        {...rest}
        loadOptions={loadOptions ? debouncedLoadOptionsAsync : normalLoadOptions}
        invalid={isInvalid}
        onChange={(e: any) => (isMulti ? onMultiSelectChange(e) : onChange(e))}
        onBlur={onBlur}
        defaultOptions={options}
        animateDropdownIcon={!loadOptions}
        defaultValue={defaultValues}
        controlShouldRenderValue={!hideSelectedValues}
        isMulti={isMulti}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        menuIsOpen={menuIsOpen}
        {...generateTestIdClassName()}
      />
      <SelectErrorMessageContainer invalid={isInvalid} requiredErrorMessage={rest.requiredErrorMessage} />
    </>
  );
});

export const SelectObsolete = React.forwardRef<HTMLElement, Omit<IBaseMultiSelectProps, 'ref' | 'isMulti'>>(function Select({ ...props }, ref) {
  const { generateTestId } = useTestId(props, 'SelectObsolete');
  return <BaseMultiSelect isMulti={false} {...props} ref={ref} {...generateTestId()} />;
});

export const MultiSelectObsolete = React.forwardRef<HTMLElement, Omit<IBaseMultiSelectProps, 'ref' | 'isMulti'>>(function MultiSelect({ ...props }, ref) {
  const { generateTestId } = useTestId(props, 'MultiSelectObsolete');
  return <BaseMultiSelect {...props} ref={ref} {...generateTestId()} />;
});
